import React from "react";
import { InlineMath } from "react-katex";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\frac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function mixedLatex({ whole, numerator, denominator }) {
  if (!numerator) return `${whole}`;
  const sign = whole < 0 ? "-" : "";
  return `${sign}${Math.abs(whole)}\\frac{${numerator}}{${denominator}}`;
}

function asMixed(fraction) {
  const sign = fraction.numerator < 0 ? -1 : 1;
  const absNumerator = Math.abs(fraction.numerator);
  const whole = Math.floor(absNumerator / fraction.denominator) * sign;
  const rest = absNumerator % fraction.denominator;
  const simpleRest = normalizeFraction(rest, fraction.denominator);
  return { whole, numerator: simpleRest.numerator, denominator: simpleRest.denominator };
}

function displayLatex(fraction) {
  return fraction.display === "mixed" ? mixedLatex(asMixed(fraction)) : fractionLatex(fraction.numerator, fraction.denominator);
}

function Fraction({ fraction, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={displayLatex(fraction)} />
    </span>
  );
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function compareFractions(a, b) {
  return a.numerator * b.denominator > b.numerator * a.denominator ? "left" : "right";
}

function oppositeSide(side) {
  return side === "left" ? "right" : "left";
}

function makeTask(type, left, right, meta = {}) {
  const mode = Math.random() < 0.5 ? "greater" : "less";
  const greater = compareFractions(left, right);
  return {
    type,
    data: {
      left,
      right,
      mode,
      correct: mode === "greater" ? greater : oppositeSide(greater),
      ...meta,
    },
  };
}

function randomProperFraction(denominator) {
  const numerators = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter(
    (numerator) => gcd(numerator, denominator) === 1
  );
  return { numerator: pick(numerators), denominator };
}

function sameDenominatorTask() {
  const denominator = randInt(4, 12);
  let left = randomProperFraction(denominator);
  let right = randomProperFraction(denominator);
  while (left.numerator === right.numerator) right = randomProperFraction(denominator);
  return makeTask("FractionCompareSameDenominatorTask", left, right);
}

function sameNumeratorTask() {
  const numerator = randInt(1, 5);
  const denominators = Array.from({ length: 10 - numerator }, (_, index) => numerator + 1 + index).filter(
    (denominator) => gcd(numerator, denominator) === 1
  );
  const d1 = pick(denominators);
  let d2 = pick(denominators);
  while (d1 === d2) d2 = pick(denominators);
  return makeTask("FractionCompareSameNumeratorTask", { numerator, denominator: d1 }, { numerator, denominator: d2 });
}

function relatedDenominatorTask() {
  const pairs = [
    [2, 6],
    [3, 6],
    [4, 8],
    [5, 10],
    [6, 12],
    [7, 14],
  ];
  const [base, larger] = pick(pairs);
  const factor = larger / base;
  let left = randomProperFraction(base);
  let right = randomProperFraction(larger);
  while (left.numerator * factor === right.numerator) {
    left = randomProperFraction(base);
    right = randomProperFraction(larger);
  }
  const leftIsBase = Math.random() < 0.5;
  return makeTask(
    "FractionCompareRelatedDenominatorTask",
    leftIsBase ? left : right,
    leftIsBase ? right : left,
    { base, larger, factor }
  );
}

function commonDenominatorTask() {
  const denominators = Array.from({ length: 10 }, (_, index) => index + 7);
  const pairs = [];
  for (let i = 0; i < denominators.length; i += 1) {
    for (let j = i + 1; j < denominators.length; j += 1) {
      const a = denominators[i];
      const b = denominators[j];
      const common = lcm(a, b);
      if (b % a !== 0 && common <= 120) pairs.push([a, b]);
    }
  }
  const [d1, d2] = pick(pairs);
  let left = randomProperFraction(d1);
  let right = randomProperFraction(d2);
  while (left.numerator * d2 === right.numerator * d1) {
    left = randomProperFraction(d1);
    right = randomProperFraction(d2);
  }
  return makeTask("FractionCompareCommonDenominatorTask", left, right, { commonDenominator: lcm(d1, d2) });
}

function improperFractionTask() {
  const d1 = randInt(3, 9);
  let d2 = randInt(3, 9);
  while (d1 === d2) d2 = randInt(3, 9);
  let left = normalizeFraction(randInt(d1 + 1, d1 * 3 - 1), d1);
  let right = normalizeFraction(randInt(d2 + 1, d2 * 3 - 1), d2);
  while (
    left.denominator === 1 ||
    right.denominator === 1 ||
    Math.abs(left.numerator) <= left.denominator ||
    Math.abs(right.numerator) <= right.denominator ||
    left.numerator * right.denominator === right.numerator * left.denominator
  ) {
    left = normalizeFraction(randInt(d1 + 1, d1 * 3 - 1), d1);
    right = normalizeFraction(randInt(d2 + 1, d2 * 3 - 1), d2);
  }
  return makeTask("FractionCompareImproperTask", left, right, { commonDenominator: lcm(left.denominator, right.denominator) });
}

function mixedFractionTask() {
  const whole = randInt(1, 6);
  const d1 = randInt(3, 9);
  let d2 = randInt(3, 9);
  while (d1 === d2) d2 = randInt(3, 9);
  let leftRest = randomProperFraction(d1);
  let rightRest = randomProperFraction(d2);
  while (leftRest.numerator * rightRest.denominator === rightRest.numerator * leftRest.denominator) {
    leftRest = randomProperFraction(d1);
    rightRest = randomProperFraction(d2);
  }
  const left = { numerator: whole * d1 + leftRest.numerator, denominator: d1, display: "mixed" };
  const right = { numerator: whole * d2 + rightRest.numerator, denominator: d2, display: "mixed" };
  return makeTask("FractionCompareMixedTask", left, right, { commonDenominator: lcm(d1, d2), whole });
}

function negativeFractionTask() {
  const d1 = randInt(3, 12);
  let d2 = randInt(3, 12);
  while (d1 === d2) d2 = randInt(3, 12);
  let left = randomProperFraction(d1);
  let right = randomProperFraction(d2);
  while (left.numerator * right.denominator === right.numerator * left.denominator) {
    left = randomProperFraction(d1);
    right = randomProperFraction(d2);
  }
  return makeTask(
    "FractionCompareNegativeTask",
    { numerator: -left.numerator, denominator: left.denominator },
    { numerator: -right.numerator, denominator: right.denominator },
    { commonDenominator: lcm(left.denominator, right.denominator) }
  );
}

export function generateTasks() {
  return [
    sameDenominatorTask(),
    sameNumeratorTask(),
    relatedDenominatorTask(),
    commonDenominatorTask(),
    improperFractionTask(),
    mixedFractionTask(),
    negativeFractionTask(),
  ];
}

function FractionPair({ left, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", margin: "10px 0" }}>
      <Fraction fraction={left} />
      <span>czy</span>
      <Fraction fraction={right} />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div>
      <p>Wskaż {data.mode === "greater" ? "większą" : "mniejszą"} wartość.</p>
      <FractionPair left={data.left} right={data.right} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const options = [
    { key: "left", label: "Pierwszy ułamek", fraction: task.data.left },
    { key: "right", label: "Drugi ułamek", fraction: task.data.right },
  ];
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {options.map((option) => {
        const selected = userAnswer === option.key;
        return (
          <button
            key={option.key}
            type="button"
            className="btn-secondary"
            disabled={disabled}
            onClick={() => onChange(option.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              borderColor: selected ? "#f59e0b" : undefined,
              boxShadow: selected ? "0 0 0 2px rgba(245,158,11,0.35)" : undefined,
            }}
          >
            {option.label}: <Fraction fraction={option.fraction} />
          </button>
        );
      })}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correct;
}

export function renderAnswer({ data }) {
  const fraction = data.correct === "left" ? data.left : data.right;
  return <Fraction fraction={fraction} />;
}

export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const fraction = userAnswer === "left" ? task.data.left : task.data.right;
  return <Fraction fraction={fraction} />;
}

function explainSameDenominator({ left, right }) {
  return (
    <p>
      Mianowniki są takie same, więc porównujemy liczniki: <InlineMath math={`${left.numerator}`} /> i{" "}
      <InlineMath math={`${right.numerator}`} />. Większy licznik oznacza większy ułamek.
    </p>
  );
}

function explainSameNumerator() {
  return (
    <p>
      Liczniki są takie same. Przy takim samym liczniku większy jest ułamek z mniejszym mianownikiem,
      bo całość jest podzielona na większe części.
    </p>
  );
}

function explainRelatedDenominator({ left, right }) {
  const leftToRight = right.denominator % left.denominator === 0;
  const source = leftToRight ? left : right;
  const target = leftToRight ? right : left;
  const factor = target.denominator / source.denominator;
  return (
    <div>
      <p>Jeden mianownik jest wielokrotnością drugiego, więc wystarczy rozszerzyć jeden ułamek.</p>
      <p>
        <MathText
          math={`${fractionLatex(source.numerator, source.denominator)}=${fractionLatex(
            `${source.numerator}\\cdot ${factor}`,
            `${source.denominator}\\cdot ${factor}`
          )}=${fractionLatex(source.numerator * factor, target.denominator)}`}
        />
      </p>
      <p>Teraz porównujemy liczniki przy tym samym mianowniku.</p>
    </div>
  );
}

function explainCommonDenominator({ left, right, commonDenominator }) {
  const leftFactor = commonDenominator / left.denominator;
  const rightFactor = commonDenominator / right.denominator;
  return (
    <div>
      <p>Sprowadzamy ułamki do wspólnego mianownika.</p>
      <p>
        <MathText
          math={`${fractionLatex(left.numerator, left.denominator)}=${fractionLatex(
            `${left.numerator}\\cdot ${leftFactor}`,
            `${left.denominator}\\cdot ${leftFactor}`
          )}=${fractionLatex(left.numerator * leftFactor, commonDenominator)}`}
        />
      </p>
      <p>
        <MathText
          math={`${fractionLatex(right.numerator, right.denominator)}=${fractionLatex(
            `${right.numerator}\\cdot ${rightFactor}`,
            `${right.denominator}\\cdot ${rightFactor}`
          )}=${fractionLatex(right.numerator * rightFactor, commonDenominator)}`}
        />
      </p>
      <p>Po sprowadzeniu do wspólnego mianownika porównujemy liczniki.</p>
    </div>
  );
}

function explainMixed({ left, right, commonDenominator, whole }) {
  const leftRest = { numerator: left.numerator - whole * left.denominator, denominator: left.denominator };
  const rightRest = { numerator: right.numerator - whole * right.denominator, denominator: right.denominator };
  const leftFactor = commonDenominator / leftRest.denominator;
  const rightFactor = commonDenominator / rightRest.denominator;
  return (
    <div>
      <p>
        Obie liczby mieszane mają tę samą liczbę całości, więc porównujemy tylko części ułamkowe.
      </p>
      <p>
        Porównujemy <MathText math={fractionLatex(leftRest.numerator, leftRest.denominator)} /> i{" "}
        <MathText math={fractionLatex(rightRest.numerator, rightRest.denominator)} />.
      </p>
      <p>
        <MathText
          math={`${fractionLatex(leftRest.numerator, leftRest.denominator)}=${fractionLatex(
            `${leftRest.numerator}\\cdot ${leftFactor}`,
            `${leftRest.denominator}\\cdot ${leftFactor}`
          )}=${fractionLatex(leftRest.numerator * leftFactor, commonDenominator)}`}
        />
      </p>
      <p>
        <MathText
          math={`${fractionLatex(rightRest.numerator, rightRest.denominator)}=${fractionLatex(
            `${rightRest.numerator}\\cdot ${rightFactor}`,
            `${rightRest.denominator}\\cdot ${rightFactor}`
          )}=${fractionLatex(rightRest.numerator * rightFactor, commonDenominator)}`}
        />
      </p>
      <p>Po sprowadzeniu do wspólnego mianownika porównujemy liczniki części ułamkowych.</p>
    </div>
  );
}

function explainNegative({ left, right, commonDenominator }) {
  return (
    <div>
      {explainCommonDenominator({ left, right, commonDenominator })}
      <p>
        Przy liczbach ujemnych kolejność jest odwrócona: liczba położona dalej od zera jest mniejsza.
      </p>
    </div>
  );
}

export function renderExplanation({ type, data }) {
  const selected = data.correct === "left" ? data.left : data.right;
  const body =
    type === "FractionCompareSameDenominatorTask"
      ? explainSameDenominator(data)
      : type === "FractionCompareSameNumeratorTask"
      ? explainSameNumerator(data)
      : type === "FractionCompareRelatedDenominatorTask"
      ? explainRelatedDenominator(data)
      : type === "FractionCompareMixedTask"
      ? explainMixed(data)
      : type === "FractionCompareNegativeTask"
      ? explainNegative(data)
      : explainCommonDenominator(data);

  return (
    <div>
      {body}
      <p>
        Polecenie pyta o wartość {data.mode === "greater" ? "większą" : "mniejszą"}, więc wybieramy{" "}
        <Fraction fraction={selected} />.
      </p>
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  FractionCompareSameDenominatorTask: taskAdapter,
  FractionCompareSameNumeratorTask: taskAdapter,
  FractionCompareRelatedDenominatorTask: taskAdapter,
  FractionCompareCommonDenominatorTask: taskAdapter,
  FractionCompareImproperTask: taskAdapter,
  FractionCompareMixedTask: taskAdapter,
  FractionCompareNegativeTask: taskAdapter,
};
